/**
 * AUTO-GENERATED BASE HOOK UTILITIES - DO NOT EDIT
 * Centralized factory functions for creating hooks
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  HookOptions,
  QueryHookOptions,
  MutationHookReturn,
  QueryHookReturn,
  PaginatedQueryHookReturn
} from '@/types/hook-generated.types';
import type { ApiError } from '@/types/infrastructure.types';
import type { MessageResponse } from '@zlinkx/shared-v2';

type PaginatedResponse<TItem> = {
  data?: TItem[];
  pagination: { nextCursor: string | null; hasMore: boolean; total?: number };
};

function requireData<TData>(response: { data?: TData }): TData {
  if (response.data === undefined) {
    throw new Error('API response is missing required data payload');
  }
  return response.data;
}


/**
 * Factory function for creating mutation hooks
 * Expects API functions that return responses with a .data property.
 * Returns standard TanStack Query mutation object with mutate/mutateAsync methods
 */
export function createMutationHook<TData, TParams, TRequest = TParams>(
  mutationFn: (params: TRequest) => Promise<{ data: TData }>,
  options: {
    queryKey?: readonly unknown[];
    defaultSuccessMessage?: string;
    defaultErrorMessage?: string;
    transformParams?: (params: TParams) => TRequest;
  } = {}
) {
  return (hookOptions?: HookOptions<TData>): MutationHookReturn<TData, TParams> => {
    const queryClient = useQueryClient();

    // Wrap the mutation function to extract data from response.
    const unwrappedMutationFn = async (params: TParams): Promise<TData> => {
      const requestParams = options.transformParams ? options.transformParams(params) : params as unknown as TRequest;
      const response = await mutationFn(requestParams);
      return requireData(response);
    };

    const mutation = useMutation<TData, ApiError, TParams>({
      mutationFn: unwrappedMutationFn,
      onSuccess: async (data) => {
        if (options.queryKey) {
          await queryClient.invalidateQueries({ queryKey: options.queryKey });
        }

        await hookOptions?.onSuccess?.(data);
      },
      onError: (error) => {
        hookOptions?.onError?.(error);
      },
    });

    return mutation;
  };
}

/**
 * Factory function for creating query hooks with parameters
 * Expects API functions that return responses with a .data property.
 */
export function createQueryHook<TData, TParams extends Record<string, unknown>>(
  queryFn: (params: TParams) => Promise<{ data: TData }>,
  createQueryKey: (params: TParams) => readonly unknown[]
) {
  return (params: TParams, options?: QueryHookOptions): QueryHookReturn<TData> => {
    // Wrap the query function to extract data from response.
    const unwrappedQueryFn = async (): Promise<TData> => {
      const response = await queryFn(params);
      return requireData(response);
    };

    const query = useQuery<TData, ApiError>({
      queryKey: createQueryKey(params),
      queryFn: unwrappedQueryFn,
      ...options,
    });

    return query;
  };
}

/**
 * Factory function for creating simple query hooks without parameters
 * Expects API functions that return responses with a .data property.
 */
export function createSimpleQueryHook<TData>(
  queryFn: () => Promise<{ data: TData }>,
  queryKey: readonly unknown[]
) {
  return (options?: QueryHookOptions): QueryHookReturn<TData> => {
    // Wrap the query function to extract data from response.
    const unwrappedQueryFn = async (): Promise<TData> => {
      const response = await queryFn();
      return requireData(response);
    };

    const query = useQuery<TData, ApiError>({
      queryKey,
      queryFn: unwrappedQueryFn,
      ...options,
    });

    return query;
  };
}

/**
 * Factory function for creating paginated query hooks (list endpoints)
 * Uses useInfiniteQuery for cursor-based pagination
 * Automatically manages page accumulation and cursor tracking
 */
export function createPaginatedQueryHook<TItem, TParams extends Record<string, unknown>>(
  queryFn: (params: TParams) => Promise<PaginatedResponse<TItem>>,
  createQueryKey: (params: TParams) => readonly unknown[]
) {
  return (params: TParams, options?: QueryHookOptions): PaginatedQueryHookReturn<TItem> => {
    const query = useInfiniteQuery<PaginatedResponse<TItem>, ApiError>({
      queryKey: createQueryKey(params),
      queryFn: async ({ pageParam }) => {
        const queryParams = (params as { query?: Record<string, unknown> }).query;
        const paginatedParams = {
          ...params,
          query: { ...(queryParams ?? {}), cursor: pageParam },
        };
        return queryFn(paginatedParams as TParams);
      },
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) =>
        lastPage.pagination?.hasMore ? (lastPage.pagination.nextCursor ?? undefined) : undefined,
      ...(options?.enabled !== undefined ? { enabled: options.enabled } : {}),
      ...(options?.staleTime !== undefined ? { staleTime: options.staleTime } : {}),
      ...(options?.refetchOnWindowFocus !== undefined ? { refetchOnWindowFocus: options.refetchOnWindowFocus } : {}),
    });

    // Flatten all pages into a single array
    const data = query.data?.pages.flatMap(page => page.data ?? []) ?? [];
    const lastPage = query.data?.pages[query.data.pages.length - 1];

    return {
      data,
      pagination: lastPage?.pagination,
      isLoading: query.isLoading,
      isFetchingNextPage: query.isFetchingNextPage,
      hasNextPage: query.hasNextPage ?? false,
      isError: query.isError,
      isSuccess: query.isSuccess,
      isFetching: query.isFetching,
      error: query.error,
      fetchNextPage: () => {
        void query.fetchNextPage();
      },
      refetch: () => {
        void query.refetch();
      },
    };
  };
}

/**
 * Factory function for creating mutation hooks that return MessageResponse
 * Used for PUT, PATCH, DELETE operations that only return a success message
 * Returns standard TanStack Query mutation object with mutate/mutateAsync methods
 */
export function createMessageMutationHook<TParams, TRequest = TParams>(
  mutationFn: (params: TRequest) => Promise<MessageResponse>,
  options: {
    queryKey?: readonly unknown[];
    defaultSuccessMessage?: string;
    defaultErrorMessage?: string;
    transformParams?: (params: TParams) => TRequest;
  } = {}
) {
  return (hookOptions?: HookOptions<void>): MutationHookReturn<void, TParams> => {
    const queryClient = useQueryClient();

    // For MessageResponse, we don't return data, just void
    // Transform params if needed
    const unwrappedMutationFn = async (params: TParams): Promise<void> => {
      const requestParams = options.transformParams ? options.transformParams(params) : params as unknown as TRequest;
      await mutationFn(requestParams);
      // MessageResponse doesn't have data, just a message
      return;
    };

    const mutation = useMutation<void, ApiError, TParams>({
      mutationFn: unwrappedMutationFn,
      onSuccess: async () => {
        if (options.queryKey) {
          await queryClient.invalidateQueries({ queryKey: options.queryKey });
        }

        await hookOptions?.onSuccess?.();
      },
      onError: (error) => {
        hookOptions?.onError?.(error);
      },
    });

    return mutation;
  };
}